import { plural } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Autocomplete, AutocompleteProps, ChipTypeMap, Skeleton, TextField } from '@mui/material';
import { Suspense } from 'react';
import {
  useSuspenseAutocompleteOptions,
  useSuspenseAutocompleteSpecificValues,
} from './hooks/useAutocompleteOptions';
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';

/**
 * A mapping of data types to their corresponding value types.
 *
 * Example:
 * - A `deviceId` is a string, so `devices` maps to `string`.
 * - A `userId` is a number, so `users` maps to `number`.
 * - An `organizationId` is a number, so `organizations` maps to `number`.
 */
type DataTypeMap = {
  devices: string;
  organizations: number;
  irrigations: number;
  users: number;
  projectsPagination: string;
  currencies: number;
  serviceTypes: number;
  announcements: number;
  integrations: number;
};

export type DataAutocompleteType = keyof DataTypeMap;
type Value<T extends DataAutocompleteType> = DataTypeMap[T];

export interface DataAutocompleteProps<
  TDataType extends DataAutocompleteType,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent'],
> extends Omit<
    AutocompleteProps<Value<TDataType>, Multiple, DisableClearable, false, ChipComponent>,
    'options' | 'renderInput'
  > {
  dataType: TDataType;
  label?: string;
  error?: boolean;
  helperText?: string;
  fallback?: React.ReactNode;
  queryOptions?: Parameters<typeof useSuspenseAutocompleteOptions>[1];
  multipleCheckboxes?: boolean;
}

export const StandardAutocompleteIds = <
  TDataType extends DataAutocompleteType,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  ChipComponent extends React.ElementType,
>(
  props: DataAutocompleteProps<TDataType, Multiple, DisableClearable, ChipComponent>,
) => {
  const { _ } = useLingui();
  const {
    error,
    helperText,
    label,
    dataType,
    queryOptions,
    multipleCheckboxes,
    ...autocompleteProps
  } = props;
  let { data } = useSuspenseAutocompleteOptions(dataType, queryOptions);
  const { data: valuesData } = useSuspenseAutocompleteSpecificValues(
    dataType,
    autocompleteProps.value ?? null,
  );

  if (dataType === 'projectsPagination') {
    data = {
      // @ts-ignore
      projectsPagination: data?.projectsPagination?.items?.map((item) => ({
        ...item,
        id: item.projectId,
      })),
    };
  }

  const getLabel = () => {
    if (label) return label;
    const count = props.multiple ? 2 : 1;

    if (dataType === 'devices')
      return _(
        plural(count, {
          one: 'Dispositivo',
          other: 'Dispositivos',
        }),
      );
    if (dataType === 'organizations')
      return _(
        plural(count, {
          one: 'Organización',
          other: 'Organizaciones',
        }),
      );
    if (dataType === 'irrigations')
      return _(
        plural(count, {
          one: 'Red de canales',
          other: 'Redes de canales',
        }),
      );
    if (dataType === 'users')
      return _(
        plural(count, {
          one: 'Usuario',
          other: 'Usuarios',
        }),
      );
    if (dataType === 'projectsPagination')
      return _(
        plural(count, {
          one: 'Proyecto',
          other: 'Proyectos',
        }),
      );
    if (dataType === 'currencies')
      return _(
        plural(count, {
          one: 'Moneda',
          other: 'Monedas',
        }),
      );
    if (dataType === 'serviceTypes')
      return _(
        plural(count, {
          one: 'Servicio',
          other: 'Servicios',
        }),
      );
    if (dataType === 'announcements')
      return _(
        plural(count, {
          one: 'Anuncio',
          other: 'Anuncios',
        }),
      );
    if (dataType === 'integrations')
      return _(
        plural(count, {
          one: 'Integración',
          other: 'Integraciones',
        }),
      );
    return '';
  };

  const getOptionLabel = (value: Value<TDataType>) => {
    if (!value) return '';
    if (dataType === 'devices') {
      const devices = [...(data.devices ?? []), ...(valuesData?.devices ?? [])];
      const device = devices.find((device) => device.id === value);
      if (!device) return String(value);
      return `${device.id} - ${device.profile.name}`;
    }
    if (dataType === 'organizations') {
      const organizations = [...(data.organizations ?? []), ...(valuesData?.organizations ?? [])];
      const organization = organizations.find((organization) => organization.id === value);
      if (!organization) return String(value);
      return organization.name;
    }
    if (dataType === 'irrigations') {
      const irrigation = data.irrigations?.find((irrigation) => irrigation.id === value);
      if (!irrigation) return String(value);
      return `${irrigation.id} - ${irrigation.name}`;
    }
    if (dataType === 'users') {
      const users = [...(data.users ?? []), ...(valuesData?.users ?? [])];
      const user = users.find((user) => user.id === value);
      if (!user) return String(value);
      return `${user.id} - ${user.name} ${user.lastnames}`;
    }
    if (dataType === 'projectsPagination') {
      const project = data.projectsPagination?.items?.find((project) => project.id === value);
      if (!project) return String(value);
      return project.projectId;
    }
    if (dataType === 'currencies') {
      const currency = data.currencies?.find((user) => user.id === value);
      if (!currency) return String(value);
      return currency.name;
    }
    if (dataType === 'serviceTypes') {
      const service = data.serviceTypes?.find((user) => user.id === value);
      if (!service) return String(value);
      return service.name;
    }
    if (dataType === 'announcements') {
      const record = data.announcementsPagination?.items?.find(({ id }) => id === value);
      if (!record) return String(value);
      return `${record.id} - ${record.title}`;
    }
    if (dataType === 'integrations') {
      const record = data.integrations?.find(({ id }) => id === value);
      if (!record) return String(value);
      return `${record.id} - ${record.name}`;
    }
    return '';
  };

  const getOptions = () => {
    if (dataType === 'announcements') {
      return data.announcementsPagination?.items.map(({ id }) => id as Value<TDataType>) ?? [];
    } else if (Array.isArray(data[dataType])) {
      return data[dataType].map(({ id }) => id as Value<TDataType>);
    }
    return [];
  };

  return (
    <Autocomplete
      renderOption={
        !multipleCheckboxes
          ? undefined
          : (props, option, { selected }) => {
              const { key, style, ...optionProps } = props;
              const SelectionIcon = selected ? CheckBox : CheckBoxOutlineBlank;

              return (
                <li key={key} {...optionProps}>
                  <SelectionIcon
                    fontSize="small"
                    style={{ marginRight: 8, padding: 9, boxSizing: 'content-box' }}
                  />
                  {getOptionLabel(option)}
                </li>
              );
            }
      }
      {...autocompleteProps}
      options={getOptions()}
      getOptionKey={(option) => {
        return option;
      }}
      getOptionLabel={autocompleteProps.getOptionLabel ?? getOptionLabel}
      renderInput={(params) => (
        <TextField {...params} label={label ?? getLabel()} error={error} helperText={helperText} />
      )}
    />
  );
};

export const StandardAutocompleteFallback: typeof StandardAutocompleteIds = (props) => {
  const { error, helperText, label, dataType, queryOptions, ...autocompleteProps } = props;
  return (
    <Skeleton variant="rounded" width="100%" sx={{ ...props.sx }}>
      <Autocomplete
        {...autocompleteProps}
        getOptionLabel={() => ''}
        options={[]}
        renderInput={(params) => <TextField {...params} label="" />}
      />
    </Skeleton>
  );
};

const StandardAutocompleteIdsWithFallback = <
  TDataType extends DataAutocompleteType,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent'],
>(
  props: DataAutocompleteProps<TDataType, Multiple, DisableClearable, ChipComponent>,
) => {
  return (
    <Suspense fallback={<StandardAutocompleteFallback {...props} />}>
      <StandardAutocompleteIds {...props} />
    </Suspense>
  );
};

export default StandardAutocompleteIdsWithFallback;
